import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './style.module.css';
import { useSelector, useDispatch } from 'react-redux';
import * as Types from 'helpers/types';
import { VIEW_MEDICATION } from 'helpers/monitoringEvents';
import {
  Spinner,
  BasketBar,
  Medication,
  CustomMedication,
  Order,
  BannerMessage,
  ManualOrderNotification,
} from 'components';
import { DashboardPageContent } from 'services/content';
import { useQuery, useMonitoringEvents, useMedication, useSelectedMedication,useOrderHistory, useAnalytics } from 'hooks';
import { plusIcon, bell as bellImg } from 'assets';

const StandardDashboard = () => {
  let query = useQuery();
  let history = useHistory();
  const dispatch = useDispatch();
  const {
    medication,
    orderHistory,
    paymentJourney: { basket },
    patient: { isP9 },
    auth: { authenticated },
  } = useSelector((state) => state);
  const [showMedicationMissing, setMedicationMissing] = useState(false);
  const [showLinkedPatient, setLinkedPatient] = useState(false);
  const [postLog] = useMonitoringEvents();
  const [getMedication] = useMedication();
  const [getSelectedMedication, deleteSelectedMedication] = useSelectedMedication();
  const [getOrders] = useOrderHistory();
  const [postFloodlight] = useAnalytics();

  postFloodlight({ code: 'heyph00' });

  const deleteMed = async (id) => {
    try {
      await deleteSelectedMedication(id);
      if (basket.includes(id)) {
        dispatch({ type: Types.BASKET_ITEM_REMOVE, payload: { courseid: id, isContraceptive: false } });
      }
      dispatch({ type: Types.MEDICATION_LOADING });
      const medicationsRes = await getSelectedMedication(authenticated);
      dispatch({ type: Types.MEDICATION_SUCCESS, payload: medicationsRes });
    } catch (e) {
      dispatch({ type: Types.MEDICATION_ERROR, payload: e });
    }
  };

  const orderView = query.get('view') === 'order' ? true : false;

  useEffect(() => {
    async function wrappedGetMedication() {
      try {
        dispatch({ type: Types.MEDICATION_LOADING });
        const medicationsRes = await getMedication(authenticated);
        dispatch({ type: Types.MEDICATION_SUCCESS, payload: medicationsRes });
        postLog(VIEW_MEDICATION);
      } catch (e) {
        dispatch({ type: Types.MEDICATION_ERROR, payload: e });
      }
    }

    async function wrappedGetSelectedMedication() {
      try {
        dispatch({ type: Types.MEDICATION_LOADING });
        const medicationsRes = await getSelectedMedication(authenticated);
        dispatch({ type: Types.MEDICATION_SUCCESS, payload: medicationsRes });
      } catch (e) {
        dispatch({ type: Types.MEDICATION_ERROR, payload: e });
      }
    }

    async function wrappedGetOrders() {
      try {
        dispatch({ type: Types.ORDER_HISTORY_LOADING });
        const ordersRes = await getOrders();
        ordersRes.sort(function (a, b) {
          return new Date(b.date_requested) - new Date(a.date_requested);
        });
        dispatch({ type: Types.ORDER_HISTORY_SUCCESS, payload: ordersRes });
      } catch (e) {
        dispatch({ type: Types.ORDER_HISTORY_ERROR, payload: e });
      }
    }

    if (isP9) {
      wrappedGetMedication();
    } else {
      wrappedGetSelectedMedication();
    }
    wrappedGetOrders();
  }, [authenticated, dispatch]);

  const updateBasket = (e, courseid, isContraceptive) => {
    e.stopPropagation();
    if (basket.includes(courseid)) {
      dispatch({ type: Types.BASKET_ITEM_REMOVE, payload: { courseid: courseid, isContraceptive: isContraceptive } });
    } else {
      dispatch({ type: Types.BASKET_ITEM_ADD, payload: { courseid: courseid, isContraceptive: isContraceptive } });
    }
  };

  const displayMedication = () => {
    if (medication.loading) return <Spinner text={DashboardPageContent.medicationLoadingText} />;
    if (medication.error) return <div>Error</div>;

    if (isP9) {
      if (medication.medication.length > 0) {
        return medication.medication.map((med) => (
          <Medication med={med} updateBasket={updateBasket} basket={basket} key={med.course_id} />
        ));
      } else {
        return <div>No Medication</div>;
      }
    } else {
      return (
        <div>
          {medication.medication.length > 0 ? (
            <>
              {medication.medication.map((med) => (
                <CustomMedication
                  key={med.id}
                  med={med}
                  basket={basket}
                  deleteMed={deleteMed}
                  updateBasket={updateBasket}
                />
              ))}
              <button
                onClick={() => history.push('/pharmacy/dashboard/add-medication')}
                className={`coop-btn coop-btn--white ${styles.addMedicationButton}`}>
                <img src={plusIcon} alt="Add medication to basket" className={styles.addMedicationButtonIcon} />
                {DashboardPageContent.requestMedicationCTAText}
              </button>
            </>
          ) : (
            <div className={styles.requestMedicationBlock}>
              <p>{DashboardPageContent.requestMedicationText}</p>
              <button
                onClick={() => history.push('/pharmacy/dashboard/add-medication')}
                className="coop-btn coop-btn--primary">
                {DashboardPageContent.requestMedicationCTAText}
              </button>
            </div>
          )}
        </div>
      );
    }
  };

  const displayMedicationMissing = () => {
    return (
      <div className={styles.conditional_display}>
        <button className={styles.link_button} href="#" onClick={() => setMedicationMissing(!showMedicationMissing)}>
          {DashboardPageContent.missingMedicationText}
        </button>
        {showMedicationMissing ? (
          <div className={styles.missing_container}>
            <h2>{DashboardPageContent.missingMedicationHeaderText}</h2>
            <p
              className={styles.intro_text}
              dangerouslySetInnerHTML={{
                __html: DashboardPageContent.missingMedicationIntroText,
              }}
            />
          </div>
        ) : null}
      </div>
    );
  };

  const displayLinkedPatient = () => {
    return (
      <div>
        <button className={styles.link_button} href="#" onClick={() => setLinkedPatient(!showLinkedPatient)}>
          {DashboardPageContent.linkedPatientText}
        </button>
        {showLinkedPatient ? (
          <div className={styles.missing_container}>
            <h2>{DashboardPageContent.linkedPatientHeaderText}</h2>
            <p
              className={styles.intro_text}
              dangerouslySetInnerHTML={{
                __html: DashboardPageContent.linkedPatientIntroText,
              }}
            />
          </div>
        ) : null}
      </div>
    );
  };

  const displayOrders = () => {
    if (orderHistory.loading) return <Spinner text="Retrieving Orders" />;
    if (orderHistory.error) return <div>Error</div>;

    if (orderHistory.orders.length > 0) {
      return orderHistory.orders.map((order) => {
        const orderDate = new Date(order.date_requested);

        const orderStatuses = order.order_statuses.sort(function (a, b) {
          return new Date(b.timestamp) - new Date(a.timestamp);
        });
        return <Order key={order.order_id} order={order} orderDate={orderDate} orderStatuses={orderStatuses} />;
      });
    } else {
      return <div>No Orders</div>;
    }
  };

  return (
    <>
      <div className="coop-l-container" data-testid="standard-dashboard">
        <div className={`${styles.dashboard} coop-l-medium-8 coop-l-large-6`}>
          <ManualOrderNotification />
          <div className={styles.tabs}>
            <div className={styles.tabs__inner}>
              <div
                className={`${styles.tab__item} ${!orderView ? styles.active : styles.inactive}`}
                tabIndex="0"
                onKeyPress={(event) => (event.key === 'Enter' ? history.push('/pharmacy/dashboard') : null)}
                onClick={() => history.push('/pharmacy/dashboard')}>
                <h2>Medication</h2>
              </div>
              <div
                className={`${styles.tab__item} ${orderView ? styles.active : styles.inactive}`}
                tabIndex="0"
                onKeyPress={(event) => (event.key === 'Enter' ? history.push('/pharmacy/dashboard?view=order') : null)}
                onClick={() => history.push('/pharmacy/dashboard?view=order')}>
                <h2>Orders</h2>
              </div>
            </div>
          </div>

          <div className={orderView ? styles.order__view : styles.list_items}>
            <BannerMessage />
            {orderView ? (
              <>
                <button
                  className={styles.orderReminderButton}
                  onClick={() => history.push('/pharmacy/dashboard/order-reminder')}
                  onKeyPress={(e) => (e.key === 'Enter' ? history.push('/pharmacy/dashboard/order-reminder') : null)}>
                  <img src={bellImg} alt="bell" />
                  <p>Order Reminders</p>
                </button>
                {displayOrders()}
              </>
            ) : null}
            {!orderView && (
              <>
                {displayMedication()}
                {isP9 ? (
                  <>
                    {displayMedicationMissing()}
                    {displayLinkedPatient()}
                  </>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
      {!orderView && <BasketBar count={basket.length} />}
    </>
  );
};

export default StandardDashboard;
